import { useState, useRef, useEffect } from 'react'
import { DecorativeImages } from '@/components/Login/DecorativeImages'
import { MainImages } from '@/components/Login/MainImages'
import { LoginForm } from '@/components/Login/LoginForm'
import { RegisterForm } from '@/components/Login/RegisterForm'

const Login = () => {
  const [isRegister, setIsRegister] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [containerHeight, setContainerHeight] = useState<number>(327) // LoginForm的大概高度
  const [loginFormHeight, setLoginFormHeight] = useState<number>(0)
  const [registerFormHeight, setRegisterFormHeight] = useState<number>(0)
  const [isInitialized, setIsInitialized] = useState(false)

  const measureLoginRef = useRef<HTMLDivElement | null>(null)
  const measureRegisterRef = useRef<HTMLDivElement | null>(null)

  // 测量表单高度
  const measureFormHeight = (
    ref: React.RefObject<HTMLDivElement | null>
  ): number => {
    return ref.current?.scrollHeight || 0
  }

  // 更新表单高度缓存
  const updateFormHeights = () => {
    const loginHeight = measureFormHeight(measureLoginRef)
    const registerHeight = measureFormHeight(measureRegisterRef)

    if (loginHeight > 0) setLoginFormHeight(loginHeight)
    if (registerHeight > 0) setRegisterFormHeight(registerHeight)

    if (loginHeight > 0 && registerHeight > 0) {
      const initialHeight = isRegister ? registerHeight : loginHeight
      setContainerHeight(initialHeight)
      setIsInitialized(true)
    }
  }

  // 更新容器高度
  const updateContainerHeight = () => {
    const targetHeight = isRegister ? registerFormHeight : loginFormHeight
    if (targetHeight > 0) {
      setContainerHeight(targetHeight)
    }
  }

  // 处理LoginForm内部高度变化
  const handleLoginFormHeightChange = () => {
    if (!isRegister && !isAnimating && isInitialized) {
      setTimeout(() => {
        const newHeight = measureFormHeight(measureLoginRef)
        if (newHeight > 0) {
          setLoginFormHeight(newHeight)
          setContainerHeight(newHeight)
        }
      }, 100)
    }
  }

  // 初始化
  useEffect(() => {
    // 立即尝试测量一次
    updateFormHeights()
    // 如果立即测量失败，延迟再试
    const timer = setTimeout(updateFormHeights, 20)
    return () => clearTimeout(timer)
  }, [])

  // 响应状态变化（仅在初始化完成后）
  useEffect(() => {
    if (isInitialized && loginFormHeight > 0 && registerFormHeight > 0) {
      updateContainerHeight()
    }
  }, [loginFormHeight, registerFormHeight, isRegister, isInitialized])

  const handleSwitchToRegister = () => {
    setIsAnimating(true)
    // 先调整高度到目标高度，再切换内容
    if (registerFormHeight > 0) {
      setContainerHeight(registerFormHeight)
    }
    // 减少延迟，让切换更快
    setTimeout(() => {
      setIsRegister(true)
    }, 80) // 减少延迟时间
  }

  const handleSwitchToLogin = () => {
    setIsAnimating(true)
    // 先调整高度到目标高度，再切换内容
    if (loginFormHeight > 0) {
      setContainerHeight(loginFormHeight)
    }
    // 减少延迟，让切换更快
    setTimeout(() => {
      setIsRegister(false)
    }, 80) // 减少延迟时间
  }

  const handleTransitionEnd = () => {
    setIsAnimating(false)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[image:url('@/assets/images/login/bg_shadow_img.png'),linear-gradient(to_bottom,#EFEEFD,white,#F8F9FA)] bg-cover bg-no-repeat relative overflow-hidden">
      <DecorativeImages />
      <MainImages isRegister={isRegister} />

      {/* 隐藏的测量容器 */}
      <div
        className="fixed top-[-9999px] left-[-9999px] w-[440px] opacity-0 pointer-events-none"
        aria-hidden="true"
      >
        <div
          className="bg-white rounded-lg shadow-md p-8"
          ref={measureLoginRef}
        >
          <LoginForm
            onSwitchToRegister={() => {}}
            onHeightChange={handleLoginFormHeightChange}
            idPrefix="measure-"
          />
        </div>
        <div
          className="bg-white rounded-lg shadow-md p-8 mt-4"
          ref={measureRegisterRef}
        >
          <RegisterForm onSwitchToLogin={() => {}} idPrefix="measure-" />
        </div>
      </div>

      <div className="flex flex-col items-center relative z-10 ml-auto lg:mr-[8%] xl:mr-[13%] 2xl:mr-[15%]">
        <h1 className="mb-4 text-center">
          <span className="text-[48px] font-medium text-[#0B0B0B]">
            欢迎使用
          </span>
          <span className="text-[48px] font-bold bg-gradient-to-r from-[#0059FF] to-[#8752FF] bg-clip-text text-transparent">
            YouKnow
          </span>
        </h1>

        <div
          className="w-[440px] bg-white rounded-lg shadow-md relative"
          style={{
            height: `${containerHeight}px`,
            overflow: 'hidden',
            transition: isInitialized ? 'height 0.2s ease-out' : 'none',
          }}
        >
          <div
            className="flex absolute top-0 left-0 w-full"
            style={{
              transform: `translateX(${isRegister ? '-50%' : '0'})`,
              width: '200%',
              transition: isInitialized ? 'transform 0.2s ease-out' : 'none',
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            <div className="w-1/2 p-8">
              <LoginForm
                onSwitchToRegister={handleSwitchToRegister}
                onHeightChange={() => {}}
                idPrefix="main-"
              />
            </div>
            <div className="w-1/2 p-8">
              <RegisterForm
                onSwitchToLogin={handleSwitchToLogin}
                idPrefix="main-"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
